import React, { FC, forwardRef, useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Done } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import dynamic from "next/dynamic";
import { ControllerRenderProps } from "react-hook-form";
import {
  useAccountById,
  useAccountTeamContactById,
  useEndCustomerContactById,
} from "components/summary/hooks";
import { useRouter } from "next/router";
import MButton from "components/momentum/MButton";
import ContactDialog from "components/summary/index/Contact/ContactDialog";
import { UseFormStateReturn } from "react-hook-form/dist/types";

interface Option {
  value: string;
  name: string;
  type: "External Customer Contacts" | "Account Team";
  jobTitle: string;
}

const useStyles = makeStyles(() => ({
  listbox: {
    '&  li[aria-selected="true"]': {
      backgroundColor: "#EDFBFE",
    },
  },
  selectTitle: {
    marginLeft: 12,
    color: "#535759",
    fontWeight: 500,
    fontSize: 14,
    marginRight: 8,
  },
}));

const QVRDialogAudience: FC<
  ControllerRenderProps & UseFormStateReturn<any> & { accountId: string | undefined }
> = (props) => {
  const classes = useStyles();
  const router = useRouter();
  const { data: accountTeamData, mutate: mutateAccountTeamContact } = useAccountTeamContactById(
    (router.query.accountId as string) ?? props.accountId ?? ""
  );
  const { data: contactMasterData, mutate: mutateEndCustomerContact } = useEndCustomerContactById(
    (router.query.accountId as string) ?? props.accountId ?? ""
  );
  const { data: account } = useAccountById(
    (router.query.accountId as string) ?? props.accountId ?? ""
  );
  const [options, setOptions] = useState<Option[]>([]);

  const [value, setValue] = useState<Option[]>([]);

  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);
  const onOpen = () => setOpen(true);

  useEffect(() => {
    if (contactMasterData && accountTeamData) {
      const options: Option[] = [];
      contactMasterData.reduce((prv, cur) => {
        prv.push({
          name: `${cur.firstName} ${cur.lastName}`,
          value: cur.id,
          type: "External Customer Contacts",
          jobTitle: cur.jobTitle,
        });
        return prv;
      }, options);
      accountTeamData.reduce((prv, cur) => {
        prv.push({
          name: `${cur.firstName} ${cur.lastName}`,
          value: cur.id,
          type: "Account Team",
          jobTitle: cur.jobTitle,
        });
        return prv;
      }, options);
      setOptions(options);
    }
  }, [accountTeamData, contactMasterData]);

  useEffect(() => {
    if (props.value && options.length) {
      const intersection = options.filter((v) => props.value.includes(v.value));
      if (intersection.length) setValue(intersection);
    }
  }, [props.value, options.length]);

  const onChange = (_e: any, value: Option[]) => {
    setValue(value);
    props.onChange(value.map((x) => x.value));
  };

  const ListboxComponent = forwardRef<HTMLUListElement>(({ children, ...rest }, ref) => {
    (children as any[]).push(
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "8px 4px 0",
        }}
        key={"button"}
        role={"option"}
      >
        <MButton
          buttonType="blue-reverse"
          buttonSize={28}
          onClick={onOpen}
        >
          Add an External Customer Contact
        </MButton>
      </div>
    );
    return (
      <ul
        {...rest}
        ref={ref}
      >
        {children}
      </ul>
    );
  });
  const defaultMenu = (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        margin: "8px 4px 0",
      }}
      key={"button"}
      role={"option"}
      onMouseDown={onOpen}
    >
      <MButton
        buttonType="blue-reverse"
        buttonSize={28}
      >
        Add an External Customer Contact
      </MButton>
    </div>
  );
  return (
    <>
      <ContactDialog
        mutateFunc={() => {
          mutateAccountTeamContact();
          mutateEndCustomerContact();
        }}
        open={open}
        data={{}}
        onClose={onClose}
        type="Add New"
        accountKey={account?.accountKey}
      />
      <div style={{ display: "flex", alignItems: "center" }}>
        <Autocomplete
          classes={{ listbox: classes.listbox }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              inputProps={{
                ...params.inputProps,
              }}
              InputProps={{
                ...params.InputProps,
              }}
              error={!!props.errors?.[props.name]}
              helperText={props.errors?.[props.name]?.message}
              size={"small"}
            />
          )}
          filterOptions={(o, s) =>
            o.filter(
              (x) =>
                x.value?.toLowerCase().includes(s.inputValue.toLowerCase()) ||
                x.name?.toLowerCase().includes(s.inputValue.toLowerCase()) ||
                x.jobTitle?.toLowerCase().includes(s.inputValue.toLowerCase())
            )
          }
          options={options}
          // loading={options.length === 0}
          loadingText={"Loading..."}
          disableCloseOnSelect={true}
          groupBy={(o) => o.type}
          fullWidth={true}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          getOptionLabel={(o) => o.name}
          multiple={true}
          ListboxComponent={ListboxComponent as any}
          noOptionsText={defaultMenu}
          renderOption={(props, option, { inputValue, selected }) => {
            const regex = new RegExp(inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig");
            const spanText = option.name.replace(
              regex,
              `<span style="background-color:#FFE399; font-size: 14px">$&</span>`
            );
            const jobTitle = option.jobTitle?.replace(
              regex,
              `<span style="background-color:#FFE399; font-size: 14px">$&</span>`
            );
            return (
              <li {...props}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 14,
                        color: "#333",
                        fontWeight: 700,
                      }}
                      dangerouslySetInnerHTML={{ __html: jobTitle }}
                    />
                    &nbsp; - &nbsp;
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 14,
                        color: "#333",
                      }}
                      dangerouslySetInnerHTML={{ __html: spanText }}
                    />
                  </div>
                  {selected ? <Done style={{ color: "#007AA3" }} /> : null}
                </div>
              </li>
            );
          }}
          size={"small"}
          onChange={onChange}
          //@ts-ignore
          value={value}
        />
      </div>
    </>
  );
};

export default dynamic(() => Promise.resolve(QVRDialogAudience), {
  ssr: false,
});
